import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { DocumentData } from "firebase/firestore";
import {
  IconCalendarTick,
  IconDelete,
  IconEdit,
  IconMore,
} from "../../components/CustomIcons";
import {
  deleteChallenge,
  getUserDetails,
  updateChallengeTeams,
  updateLeagueFeedByChallengeField,
} from "./firestoreQuery";
import { useAuth } from "../../contexts/AuthContext";
import { flRatingOptions } from "../../constants/leagueLockerConfig";
import PaymentHandlerRenderer from "./paymentHandlerRenderer";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import noImg from "../../assets/images/winner-icon.png";
import { useClickOutside } from "../../hooks/useClickOutside";
import { SectionLoader } from "../ui/SectionLoader";

interface LeagueLockerChallengeSectionProps {
  challenges: DocumentData[];
  leagueUrl: string;
  leagueData: any;
  refreshLeague: boolean;
  setRefreshLeague: (refresh: boolean) => void;
  allTeams?: DocumentData[];
  challengeId?: string;
}

const LeagueLockerChallengeSection: React.FC<
  LeagueLockerChallengeSectionProps
> = ({
  challenges,
  leagueUrl,
  leagueData,
  allTeams,
  refreshLeague,
  setRefreshLeague,
}) => {
  const navigate = useNavigate();
  const user = useAuth().user;
  const [expandedChallenges, setExpandedChallenges] = useState<Set<string>>(
    new Set()
  );
  const [userDetailsCache, setUserDetailsCache] = useState<Map<string, any>>(
    new Map()
  );
  const [localChallenges, setLocalChallenges] = useState<any>(null);
  const [showMore, setShowMore] = useState(false);
  const [selectedChallenge, setSelectedChallenge] = useState<any>(null);
  const showMoreRef = React.useRef<HTMLDivElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmModalWithMode, setShowConfirmModalWithMode] = useState({
    isShowing: false,
    mode: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  // Close more options dropdown when clicking outside

  useClickOutside(showMoreRef, () => {
    if (showMore) {
      setShowMore(false);
    }
  });

  // Sync local challenges with prop changes
  useEffect(() => {
    setIsLoading(true);
    setLocalChallenges(challenges);
    setIsLoading(false);
  }, [challenges]);

  const handleCreateClick = () => {
    navigate(`/manage-challenge/${leagueUrl}`);
  };

  const toggleChallengeDetails = (challengeId: string) => {
    setExpandedChallenges((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(challengeId)) {
        newSet.delete(challengeId);
      } else {
        newSet.add(challengeId);
      }
      return newSet;
    });
  };

  const formatAmount = (value: Number) => {
    return value.toFixed(2); // Exactly 2 decimals
  };

  // Helper function to check if ACCEPT CHALLENGE button should be shown
  const shouldShowAcceptButton = (challenge: any): boolean => {
    if (!user?.uid) return false; // User must be logged in

    const challengeTeams = Array.isArray(challenge.teams)
      ? challenge.teams
      : [];
    const eligibleUser = challengeTeams.some(
      (team: any) => team.createdBy === user.uid && team.hasJoined === false
    );

    if (!eligibleUser) return false;

    const hasNotStarted =
      new Date() < new Date(challenge.selectedStartingWeekDate);
    const isCompleted =
      new Date() >
      new Date(challenge?.selectedEndingWeekDate ?? challenge.seasonEndDate);
    // Show if user eligible AND (season challenge OR not started yet)
    return (
      !isCompleted && (challenge.challengeType === "season" || hasNotStarted)
    );
  };

  // Handle accept challenge button click
  const handleAcceptChallenge = async (challenge: any) => {
    if (!user?.uid || !challenge.id) return;

    try {
      const challengeTeams = [...(challenge.teams || [])]; // Create a copy
      const userTeamIndex = challengeTeams.findIndex(
        (team: any) => team.createdBy === user.uid
      );

      if (userTeamIndex !== -1) {
        // Update the hasJoined field to true for the user's team
        challengeTeams[userTeamIndex] = {
          ...challengeTeams[userTeamIndex],
          hasJoined: true,
        };

        // Update Firebase subcollection with ONLY the single updated team (optimized)
        const updatedTeam = challengeTeams[userTeamIndex];
        const updateResult = await updateChallengeTeams(
          leagueData.id,
          challenge.id,
          updatedTeam
        );

        if (updateResult.success) {
          // Update the local challenges state with the modified teams array
          setLocalChallenges((prevChallenges: any) =>
            prevChallenges.map((c: any) =>
              c.id === challenge.id ? { ...c, teams: challengeTeams } : c
            )
          );
          console.log("Challenge accepted successfully");
        } else {
          console.error("Failed to update challenge:", updateResult.message);
        }
      }
    } catch (error) {
      console.error("Error accepting challenge:", error);
    }
  };

  // Helper function to get initials from name
  const getInitials = (name: string): string => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase())
      .slice(0, 2)
      .join("");
  };

  // Function to get creator details from challenge teams or users collection
  const getCreatorDetails = async (createdBy: string) => {
    // First check if we already have this user's details cached
    if (userDetailsCache.has(createdBy)) {
      return userDetailsCache.get(createdBy);
    }

    // Check if creator is in challenge teams array

    const teamMemberAllDetails = allTeams?.find(
      (team: any) => team.createdBy === createdBy
    );

    if (teamMemberAllDetails) {
      const details = {
        ...teamMemberAllDetails,
        initials: getInitials(teamMemberAllDetails.name),
      };
      setUserDetailsCache((prev) => new Map(prev).set(createdBy, details));
      return details;
    }

    // Fallback: fetch from users collection
    try {
      const userDetails = await getUserDetails(createdBy);
      const details = {
        name: userDetails?.name || "Unknown User",
        imageUrl: null, // No image for non-team members
        initials: getInitials(userDetails?.name || "Unknown User"),
      };
      setUserDetailsCache((prev) => new Map(prev).set(createdBy, details));
      return details;
    } catch (error) {
      console.error("Error fetching user details:", error);
      const details = {
        name: "Unknown User",
        imageUrl: null,
        initials: "U",
      };
      setUserDetailsCache((prev) => new Map(prev).set(createdBy, details));
      return details;
    }
  };

  // Component to render creator info
  const CreatorInfo: React.FC<{
    createdBy: string;
    teams?: any[];
    leagueCommissioner: string;
    entryFee?: number;
  }> = ({ createdBy, teams = [], leagueCommissioner, entryFee }) => {
    const [creatorDetails, setCreatorDetails] = useState<any>(null);

    useEffect(() => {
      const fetchDetails = async () => {
        const details = await getCreatorDetails(createdBy);
        setCreatorDetails(details);
      };
      fetchDetails();
    }, [createdBy, teams]);

    if (!creatorDetails) {
      return (
        <div className="createdUserDetail">
          <div
            className={`userPic ${
              createdBy === leagueCommissioner ? "commissionerPic" : ""
            }`}
          >
            <div className="userInitials">...</div>
          </div>
          <div className="userInfo">
            <span>Created by</span>
            <strong>Loading...</strong>
          </div>
        </div>
      );
    }

    return (
      <div className="createdByBlk">
        <div className="createdUserDetail" title={creatorDetails.name}>
          <div
            className={`userPic ${
              createdBy === leagueCommissioner ? "commissionerPic" : ""
            }`}
          >
            {creatorDetails.imageUrl ? (
              <img src={creatorDetails.imageUrl} alt={creatorDetails.name} />
            ) : (
              <div className="userInitials">{creatorDetails.initials}</div>
            )}
          </div>
          <div className="userInfo">
            <span>Created by</span>
            <strong>{creatorDetails.name}</strong>
          </div>
        </div>
        <PaymentHandlerRenderer team={creatorDetails} entryFee={entryFee} />
      </div>
    );
  };

  const getPunishmentLevel = (flRating?: string | null) => {
    if (!flRating) return null;
    const level = flRatingOptions.findIndex(
      (option) => option.value === flRating
    );
    if (level !== -1) {
      const labelPart =
        flRatingOptions[level].label &&
        flRatingOptions[level].label[1] &&
        flRatingOptions[level].label[1].split("|")[0].trim();
      return labelPart ? (
        <div className={`levelBlk badge-${level + 1}`}>{labelPart}</div>
      ) : null;
    }
    return null;
  };

  // Show all published challenges plus draft challenges authored by current user
  const filteredChallenges = localChallenges?.filter(
    (ch: any) =>
      ch?.challengeCreationStatus === "published" ||
      (ch?.challengeCreationStatus === "draft" && ch?.createdBy === user?.uid)
  );

  const getChallengeStatus = (
    challenge: any
  ): { message: string; className: string } => {
    if (challenge.challengeCreationStatus === "draft") {
      return { message: "Draft", className: "statusDraft" };
    }
    if (challenge.challengeCreationStatus === "published") {
      const currentDate = new Date();
      const startDate = new Date(
        challenge.challengeType === "weekly"
          ? challenge.selectedStartingWeekDate
          : challenge.seasonStartDate
      );
      const endDate = new Date(
        challenge.challengeType === "weekly"
          ? challenge.selectedEndingWeekDate
          : challenge.seasonEndDate
      );

      if (currentDate < startDate) {
        return {
          message: ` 🗓️ Starts ${startDate.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          })}`,
          className: "",
        };
      } else if (currentDate >= startDate && currentDate <= endDate) {
        return { message: "🔥 In Progress", className: "statusInProgress" };
      } else {
        return { message: "✅ Completed", className: "statusComplete" };
      }
    }
    return { message: "Unknown Status", className: "statusUnknown" };
  };

  const isEligibleForMoreAction = (challenge: any) => {
    // Only the creator can edit/delete, and only before the challenge starts (unless it's a draft or season challenge)
    if (challenge.createdBy !== user?.uid) {
      return false;
    }

    if (challenge.challengeCreationStatus === "draft") {
      return true;
    }

    if (challenge.challengeType === "season") {
      return true;
    }

    // For published weekly challenges, allow edit/delete only before start date
    const now = new Date();
    const startDate = new Date(challenge.selectedStartingWeekDate);
    return now < startDate;
  };

  const handleChallengeDelete = async () => {
    if (!selectedChallenge || !isEligibleForMoreAction(selectedChallenge))
      return;

    try {
      setShowMore(false);
      setIsSubmitting(true);
      await deleteChallenge(leagueData.id, selectedChallenge.id);
      setIsLoading(true);
      await updateLeagueFeedByChallengeField(
        leagueData.id,
        selectedChallenge.id,
        { challengeCreationStatus: "deleted" }
      );
      setRefreshLeague(!refreshLeague);
    } catch (error) {
      console.error("Error deleting challenge:", error);
    } finally {
      setIsSubmitting(false);
      setShowConfirmModalWithMode(() => ({ isShowing: false, mode: "" }));
      setSelectedChallenge(null);
      setIsLoading(false);
    }
  };

  const handleChallengeEdit = () => {
    if (!selectedChallenge || !isEligibleForMoreAction(selectedChallenge))
      return;
    try {
      setShowMore(false);
      setIsSubmitting(true);
      navigate(`/manage-challenge/${leagueUrl}/${selectedChallenge.id}`);
      setRefreshLeague(!refreshLeague);
    } catch (error) {
      console.error("Error editing challenge:", error);
    } finally {
      setIsSubmitting(false);
      setShowConfirmModalWithMode(() => ({ isShowing: false, mode: "" }));
      setSelectedChallenge(null);
    }
  };
  const getModalButtonText = () => {
    if (showConfirmModalWithMode.mode === "delete") {
      return isSubmitting ? "Deleting..." : "Delete";
    } else if (showConfirmModalWithMode.mode === "edit") {
      return "Edit";
    }
    return "Confirm";
  };

  const handleDisableCreateChallenge = () => {
    if (!user) {
      return true;
    }
    if (allTeams && allTeams?.length < +leagueData.leagueSize) {
      return true;
    }
    if (!allTeams?.some((team: any) => team.createdBy === user?.uid)) {
      return true;
    } else {
      return false;
    }
  };

  if (isLoading || filteredChallenges === null) {
    return <SectionLoader />;
  }

  return (
    <div className="contentArea otherContentArea">
      <div className="wrapper">
        <div className="yourLeagueHeader stickyHeader">
          <div className="headingItem">Challenges</div>
          <div className="actionItems">
            <button
              type="button"
              className="btnStyle btnFilled"
              onClick={handleCreateClick}
              disabled={handleDisableCreateChallenge()}
            >
              CREATE
            </button>
          </div>
        </div>

        <ul className="challengeCard">
          {filteredChallenges?.length === 0 && (
            <li>No challenges available. Create one to get started!</li>
          )}

          {filteredChallenges?.map((challenge: any, idx: number) => {
            const challengeId = challenge.id ?? `challenge-${idx}`;
            const isExpanded = expandedChallenges.has(challengeId);
            const currentTotalPot =
              Number(challenge?.challengeStakes?.entryFee ?? 0) *
              challenge?.teams?.filter((team: any) => team.hasJoined).length;
            return (
              <li key={challengeId}>
                {isEligibleForMoreAction(challenge) && (
                  <div className="feedBlkTop">
                    <span></span>
                    <div className="moreActionWrap">
                      <span
                        className="actionOption"
                        onClick={() => {
                          setSelectedChallenge(challenge);
                          setShowMore((prev) => !prev);
                        }}
                      >
                        <IconMore />
                      </span>
                      {challenge?.id === selectedChallenge?.id && showMore && (
                        <div className="moreOptionWrap" ref={showMoreRef}>
                          <button
                            onClick={() => {
                              setShowConfirmModalWithMode(() => ({
                                isShowing: true,
                                mode: "edit",
                              }));
                            }}
                            disabled={isSubmitting}
                          >
                            <IconEdit />
                            <span>Edit Challenge</span>
                          </button>
                          <button
                            onClick={() => {
                              setShowConfirmModalWithMode(() => ({
                                isShowing: true,
                                mode: "delete",
                              }));
                            }}
                            disabled={isSubmitting}
                          >
                            <IconDelete />
                            <span>Delete Challenge</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <div className="challengeHeading">
                  <span>{challenge.challengeTitle || ""}</span>
                </div>
                <div className="challengeTopBlk">
                  <div className="challengeImg">
                    <img
                      src={
                        challenge.challengeImageUrl
                          ? challenge.challengeImageUrl
                          : noImg
                      }
                      alt=""
                    />
                  </div>
                  <div className="challengeDesc">
                    <div className="prose">
                      <Markdown remarkPlugins={[remarkGfm]}>
                        {challenge.challengeDescription || ""}
                      </Markdown>
                    </div>
                    <div
                      className={`challengeStatus ${
                        getChallengeStatus(challenge).className
                      }`}
                    >
                      {getChallengeStatus(challenge).message}
                    </div>
                  </div>
                </div>
                <p>Teams who have already accepted the challenge</p>
                <ul className="userList">
                  {challenge.teams
                    ?.filter((eachTeam: any) => eachTeam.hasJoined)
                    .map((team: any, index: number) => {
                      const teamDetails = allTeams?.find(
                        (teamMember) => teamMember.createdBy === team.createdBy
                      );
                      return (
                        <li
                          className={`${
                            team.createdBy === leagueData.createdBy
                              ? "commissionerPic"
                              : ""
                          }`}
                          key={teamDetails?.createdBy || index}
                          title={teamDetails?.name}
                        >
                          {teamDetails?.imageUrl ? (
                            <img
                              src={teamDetails?.imageUrl}
                              alt={teamDetails?.name}
                            />
                          ) : (
                            <div className="userInitials">
                              {getInitials(teamDetails?.name)}
                            </div>
                          )}
                        </li>
                      );
                    })}
                </ul>
                <CreatorInfo
                  createdBy={challenge.createdBy}
                  entryFee={challenge.challengeStakes?.entryFee}
                  teams={allTeams || []}
                  leagueCommissioner={leagueData.createdBy}
                />
                {isExpanded && (
                  <div className="challengeDetailsBlk">
                    <div className="blkHeading">The Stakes</div>
                    <div className="priceContWrap">
                      <div className="priceBlk">
                        <strong>
                          $
                          {challenge.challengeStakes?.entryFee != null
                            ? Number(
                                challenge.challengeStakes.entryFee
                              ).toLocaleString()
                            : "0"}
                        </strong>
                        <span>Entry Fee</span>
                      </div>
                      <div className="priceBlk">
                        <strong>${currentTotalPot.toLocaleString()}</strong>
                        <span>Total Pot</span>
                      </div>
                    </div>
                    <ul className="stackList">
                      {challenge.challengeStakes?.distribution.map(
                        (pos: any, idx: number) => {
                          if (pos.place === "Last Place") {
                            return null;
                          }
                          const getPlaceEmoji = (placeText: string) => {
                            switch (placeText) {
                              case "1st Place":
                                return "🏆 ";
                              case "2nd Place":
                                return "🥈 ";
                              case "3rd Place":
                                return "🥉 ";
                              default:
                                return "";
                            }
                          };
                          return (
                            <li key={`stake-${idx}`}>
                              <span>
                                {getPlaceEmoji(pos.place)}
                                {pos.place}:
                              </span>
                              <strong>
                                $
                                {formatAmount(
                                  Number(
                                    (pos.percentage * currentTotalPot) / 100
                                  )
                                ).toLocaleString() ?? "0"}
                              </strong>
                            </li>
                          );
                        }
                      )}
                    </ul>
                    <hr />
                    {challenge.punishment && (
                      <>
                        <div className="blkHeading">Last Place Punishment</div>
                        <div className="punishmentBlk">
                          <div className="punishmentPic">
                            <img
                              src={
                                challenge.punishment.cardImage ??
                                "https://firebasestorage.googleapis.com/v0/b/fantasyloser-f6991.appspot.com/o/default_images%2F1752476554149_cropped-image.jpg?alt=media&token=376ac225-1be3-4e4f-b528-ac1999ac6f58"
                              }
                              alt=""
                            />
                          </div>
                          <div className="punishmentDetail">
                            <strong>{challenge.punishment.title}</strong>
                            <span>{challenge.punishment.subtitle}</span>
                            {getPunishmentLevel(challenge.punishment?.flRating)}
                          </div>
                        </div>
                      </>
                    )}

                    {challenge?.additionalDetails && (
                      <>
                        <div className="blkHeading">Additional Details</div>
                        <div className="additionalDetailBlk">
                          <div className="prose">
                            <Markdown remarkPlugins={[remarkGfm]}>
                              {challenge?.additionalDetails}
                            </Markdown>
                          </div>
                        </div>
                        <hr />
                      </>
                    )}
                    {shouldShowAcceptButton(challenge) && (
                      <div className="blkHeading">Ready to Accept?</div>
                    )}
                    <div className="termsBlkWrap">
                      <div className="termsBlk">
                        <IconCalendarTick />
                        <span>Challenge Starts</span>
                        <strong>
                          {challenge.challengeType === "weekly" && (
                            <>Week {challenge?.selectedStartingWeek} &bull; </>
                          )}
                          {new Date(
                            challenge?.selectedStartingWeekDate ??
                              challenge?.seasonStartDate
                          ).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })}
                        </strong>
                      </div>
                      <div className="termsBlk">
                        <span>Challenge Ends</span>
                        <strong>
                          {challenge.challengeType === "weekly" && (
                            <>Week {challenge?.selectedEndingWeek} &bull; </>
                          )}
                          {new Date(
                            challenge?.selectedEndingWeekDate ??
                              challenge?.seasonEndDate
                          ).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })}
                        </strong>
                      </div>
                    </div>
                  </div>
                )}
                <div className="btnWrap">
                  <button
                    className="btnStyle btnFilled"
                    onClick={() => toggleChallengeDetails(challengeId)}
                  >
                    {isExpanded ? "VIEW LESS" : "VIEW DETAILS"}
                  </button>
                  {shouldShowAcceptButton(challenge) && (
                    <button
                      className="btnStyle btnHighlighted"
                      onClick={() => handleAcceptChallenge(challenge)}
                    >
                      ACCEPT CHALLENGE
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
      {showConfirmModalWithMode.isShowing && (
        <div className="modalWrap">
          <div className="modalContent">
            {showConfirmModalWithMode.mode === "delete" ? (
              <span>
                Are you sure you want to delete{" "}
                <strong>{selectedChallenge.challengeTitle}</strong>?
              </span>
            ) : (
              <>
                {selectedChallenge.challengeCreationStatus === "published" && (
                  <p>
                    Editing this challenge will require all participants to
                    re-accept it.
                  </p>
                )}
                <p>Do you wish to continue?</p>
              </>
            )}
            <div className="btnWrap">
              <button
                className="btnStyle"
                type="button"
                onClick={() => {
                  setShowConfirmModalWithMode(() => ({
                    isShowing: false,
                    mode: "",
                  }));
                  setSelectedChallenge(null);
                }}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                className="btnStyle btnFilled"
                type="button"
                onClick={
                  showConfirmModalWithMode.mode === "delete"
                    ? handleChallengeDelete
                    : handleChallengeEdit
                }
                disabled={isSubmitting}
              >
                {getModalButtonText()}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeagueLockerChallengeSection;
